/* eslint-disable import/order */
/**
 * v2 fork of Reports/.../DriverAnalysis/GenerateChartPanel.
 *
 * Compression vs v1: drop in-panel `h6` title (Driver Analysis card chip
 * already labels the surface), drop the empty download-button placeholder
 * Box, tighten container margins, Typography cell wraps at 2 lines
 * instead of 3. Uses the v2 fork of RightOnlyBarCell (28px bar vs v1's
 * 40px). Pre-AI Reports DA panel stays at v1 sizes.
 */
import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { Box, Container, Slide, Typography } from '@mui/material';
import { find, get } from 'lodash';

import { ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { ColDef } from 'ag-grid-community';

import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import RightOnlyBarCell from './RightOnlyBarCell';
import { truncationValues } from 'src/components/Visualization/visualizationConstants';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';

export interface DriverAnalysisChartData {
  ModelID: string;
  Input_Name: string;
  Impact: string;
  Relative_Impact: number;
  Input_Category: string;
  Change: number[];
}

export interface IDriverAnalysisChartStateData {
  ModelID: string;
  Impact: DriverAnalysisChartData['Impact'];
  Input_Name: DriverAnalysisChartData['Input_Name'];
  Relative_Impact: DriverAnalysisChartData['Relative_Impact'];
  Input_Category: DriverAnalysisChartData['Input_Category'];
  Change: DriverAnalysisChartData['Change'];
}

interface GenerateChartPanelProps {
  data: DriverAnalysisChartData[];
  open: boolean;
  formatKey: number | null | undefined;
}

const GenerateChartPanel: FunctionComponent<GenerateChartPanelProps> = ({
  data,
  open,
  formatKey,
}) => {
  const [state, setState] = React.useState<{
    reportsData: IDriverAnalysisChartStateData[];
  }>({
    reportsData: [],
  });

  useEffect(() => {
    const reportsData = data.map((d, index) => ({
      ...d,
      ModelID: String(index),
      Change: [d.Relative_Impact],
    }));
    setState({ reportsData });
  }, [data]);

  const gridRef = useRef<AgGridReact<any>>(null);

  const sizeToFit = () => {
    gridRef.current?.api.sizeColumnsToFit();
  };

  const magnitudeTruncation = get(
    find(formatParameters, {
      formatKey,
    }) as IFormatOutputParameters,
    'magnitudeTruncation',
    0
  );

  const decimalPlacesValue = get(
    find(formatParameters, {
      formatKey,
    }) as IFormatOutputParameters,
    'decimalPlaces',
    0
  );

  const formattedImpact = useMemo(() => {
    return (value: number) =>
      (value / truncationValues[String(magnitudeTruncation)]).toFixed(
        decimalPlacesValue
      );
  }, [truncationValues, magnitudeTruncation, decimalPlacesValue]);

  const ModelRenderer = (
    cellRendererParams: ICellRendererParams<IDriverAnalysisChartStateData>
  ) => {
    const modelId = cellRendererParams.data?.ModelID;
    const data = state.reportsData;
    const model = find(data, { ModelID: modelId });
    const field = cellRendererParams?.colDef
      ?.field as keyof IDriverAnalysisChartStateData;
    return model ? (
      <Typography
        component="span"
        variant="body2"
        sx={{
          textWrap: 'auto',
          WebkitLineClamp: 2,
          textOverflow: 'ellipsis',
          maxHeight: 'calc(1.4em*2)',
          overflow: 'hidden',
          // Plain block; each column's `cellStyle` flex-centers the
          // Typography within its 40px AG cell so text and bar share a
          // common vertical centerline.
          display: 'block',
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        {field === 'Impact'
          ? formattedImpact(Number(model[field]))
          : model[field]}
        {field === 'Relative_Impact' ? '%' : ''}
      </Typography>
    ) : (
      ''
    );
  };

  const maxAbsChange = Math.max(
    100,
    ...state.reportsData.map((r) => {
      const raw = Array.isArray(r.Change) ? r.Change[0] : r.Change;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.abs(n) : 0;
    })
  );

  // Flex-center cell content vertically so text + bar share a common
  // centerline within the 40px row. Applies to every column.
  const centeredCellStyle = { display: 'flex', alignItems: 'center' };

  const reportColumns: ColDef<IDriverAnalysisChartStateData>[] = [
    {
      field: 'Input_Category',
      headerName: 'Input Category',
      headerTooltip: 'Input Category',
      sortable: true,
      cellRenderer: ModelRenderer,
      cellStyle: centeredCellStyle,
      flex: 15,
      minWidth: 100,
    },
    {
      field: 'Input_Name',
      headerName: 'Input Name',
      headerTooltip: 'Input Name',
      sortable: true,
      cellRenderer: ModelRenderer,
      cellStyle: centeredCellStyle,
      flex: 20,
      minWidth: 100,
    },
    {
      field: 'Impact',
      headerName: 'Impact',
      headerTooltip: 'Impact',
      cellRenderer: ModelRenderer,
      cellStyle: centeredCellStyle,
      sortable: true,
      flex: 15,
      minWidth: 100,
    },
    {
      field: 'Relative_Impact',
      headerName: 'Relative Impact (%)',
      headerTooltip: 'Relative Impact (%)',
      cellRenderer: ModelRenderer,
      cellStyle: centeredCellStyle,
      flex: 20,
      minWidth: 140,
    },
    {
      field: 'Change',
      headerTooltip: 'Change',
      headerName: 'Change',
      valueGetter: (p: any) => {
        const raw = Array.isArray(p.data.Change)
          ? p.data.Change[0]
          : p.data.Change;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      },
      cellRenderer: RightOnlyBarCell,
      cellRendererParams: {
        maxAbs: maxAbsChange,
        negColor: '#C23531',
        posColor: '#1271C4',
      },
      cellStyle: centeredCellStyle,
      sortable: true,
      flex: 30,
      minWidth: 260,
    },
  ];
  return (
    // No pb here — the card wrapper itself already adds ~20px of chrome
    // below this Box, which roughly matches the ~14px above the table
    // from `Container.mt: 1` + the card's own chip-row chrome. Adding
    // explicit pb here pushed the bottom whitespace to ~32px, much
    // larger than the top.
    <Box>
      <Slide direction="right" in={open} mountOnEnter unmountOnExit>
        <Container
          maxWidth={false}
          sx={{
            mb: 0,
            mt: 1,
            pt: 0,
            height: 360,
          }}
        >
          <Box
            className="ag-theme-alpine"
            sx={{ height: '100%', width: '100%' }}
          >
            <AgGridReact<IDriverAnalysisChartStateData>
              ref={gridRef}
              animateRows
              rowData={state.reportsData}
              columnDefs={reportColumns}
              defaultColDef={{ suppressMenu: true, resizable: false }}
              domLayout="normal"
              rowHeight={40}
              pagination={false}
              suppressRowClickSelection
              suppressPaginationPanel
              suppressMovableColumns
              suppressContextMenu
              onGridReady={sizeToFit}
              enableBrowserTooltips
            />
          </Box>
        </Container>
      </Slide>
    </Box>
  );
};

export default GenerateChartPanel;
